export type EpgImageObject = {
  readonly baseUrl: string | null;
  readonly defaultUrl?: string;
  readonly originalHeight?: number;
  readonly originalWidth?: number;
  readonly all?: Array<string>;
};

type EpgEntryType = 'epg-catchup' | 'rtlplus-episode' | 'epg-preview';

export type EpgEntry = {
  readonly model: {
    readonly createdAt?: string;
    readonly publishedAt?: string;
    readonly fields: {
      readonly fields: EpgEntryFields;
      readonly image?: EpgImageObject | null;
    };
    readonly lastmodifiedAt?: string;
    readonly url?: string;
  };
  readonly type?: EpgEntryType;
};

export type EpgEntryFields = {
  readonly identifier: string;
  readonly bsoKey?: string;
  readonly name: string;
  readonly alternateName?: string | null;
  readonly broadcastService?: string | null;
  readonly abstract?: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly firstAiredDate?: string;
  readonly description?: string | null;
  readonly episodeNumber?: number | null;
  readonly caption?: boolean;
  readonly url?: string;
  readonly previousEpisodeUrl?: string | null;
  readonly nextEpisodeUrl?: string | null;
  readonly ['parental.ratingMinAge']?: string;
  readonly ['rtlPlus.duration']?: string;
  readonly workFeatured?: ReadonlyArray<{
    readonly name?: string;
  }>;
  readonly offers?: ReadonlyArray<{
    readonly seller?: {
      readonly name?: string;
    };
  }>;
};

export type ProgressData = {
  readonly progressValue: number;
  readonly progressMax: number;
  readonly remainingMs: number;
  readonly remainingLabel: string;
  readonly isPlaying: boolean;
};

export const getProgressData = (startISO: string, endISO: string, nowMs: number = Date.now()): ProgressData => {
  const startMs = new Date(startISO).getTime();
  const endMs = new Date(endISO).getTime();
  const totalMs = endMs - startMs;

  const elapsedMs = Math.min(Math.max(0, nowMs - startMs), totalMs);
  const remainingMs = Math.max(0, endMs - nowMs);

  return {
    progressValue: Math.round(elapsedMs / 1_000),
    progressMax: Math.round(totalMs / 1_000),
    remainingMs,
    remainingLabel: formatRemaining(remainingMs),
    isPlaying: nowMs >= startMs && nowMs < endMs,
  };
};

const formatRemaining = (ms: number): string => {
  const totalMinutes = Math.ceil(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `Noch ${totalMinutes} ${totalMinutes === 1 ? 'Minute' : 'Minuten'}`;
  }

  const hoursPart = `${hours} Std.`;
  const minutesPart = minutes > 0 ? ` ${minutes} Min.` : '';

  return `Noch ${hoursPart}${minutesPart}`;
};

const DayParts = {
  Night: 'Nachts',
  Morning: 'Morgens',
  Afternoon: 'Mittags / Nachmittags',
  Evening: 'Abends',
} as const;

type DayPart = (typeof DayParts)[keyof typeof DayParts];

export const getDayPart = (d: Date): DayPart => {
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');

  if (h < 6) return DayParts.Night;
  if (h < 12) return DayParts.Morning;
  if (h < 20 || (h === 20 && m < 15)) return DayParts.Afternoon;
  return DayParts.Evening;
};

export type EpgSection = {
  readonly part: DayPart;
  readonly items: Array<EpgEntry>;
};

export const groupByDayPart = (items: ReadonlyArray<EpgEntry>): ReadonlyArray<EpgSection> => {
  const normalized = items
    .map((item) => {
      const rawDate = item.model.fields.fields.startDate;
      const parsedDate = new Date(rawDate);

      return {
        item,
        parsedDate,
      };
    })
    .filter((entry) => !isNaN(entry.parsedDate.getTime()));

  const sorted = normalized?.toSorted((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

  const sections: Array<EpgSection> = [];
  let current: EpgSection | undefined;

  for (const { item, parsedDate } of sorted) {
    const part = getDayPart(parsedDate);

    if (!current || current.part !== part) {
      current = { part, items: [] };
      sections.push(current);
    }

    current.items.push(item);
  }

  return sections;
};

const showAbbreviations: Record<string, string> = {
  'Gute Zeiten, schlechte Zeiten': 'GZSZ',
  'Alles was zählt': 'AWZ',
};

export const getAbbreviation = (title: string) => {
  return showAbbreviations[title] ?? title;
};
